import type { ReactNode } from "react";
import { memo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bot, Settings } from "lucide-react";
import { Urls } from "@/lib/navigation";
import { trackDemosClicked } from "@/analytics";
import type { MobileIslandConfig } from "../ChatInput";
import { LandingNotices } from "../ChatInput/LandingContent";
import agenticaLogo from "../../assets/agenticabeta.svg";

interface ChatLandingViewProps {
  mobileIsland?: MobileIslandConfig;
  disconnected?: boolean;
  statusBanners: ReactNode;
  chatInput: ReactNode;
  /** Dev-only: collapse the top padding so a tall ImmediateResult has
   *  room to breathe. Stays false in production where the block (only
   *  triggered by `$$ …` commands) never renders. */
  compact?: boolean;
}

const MobileLandingIsland = memo(function MobileLandingIsland({
  mobileIsland,
}: {
  mobileIsland: MobileIslandConfig;
}) {
  return (
    <div className="absolute left-4 top-4 z-10 desktop:hidden">
      <div className="flex items-center gap-1 rounded-full border border-white/[0.08] bg-zinc-900/90 p-1 shadow-2xl backdrop-blur-xl">
        <Link
          to={Urls.agents()}
          className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors active:bg-white/[0.08]"
        >
          <Bot className="h-[20px] w-[20px] text-zinc-400" />
          {mobileIsland.runningCount > 0 && (
            <span className="absolute right-0 top-0 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-emerald-600 px-1 text-[9px] font-medium text-white">
              {mobileIsland.runningCount}
            </span>
          )}
        </Link>
        <div className="h-4 w-px bg-white/[0.06]" />
        <Link
          to={Urls.settings()}
          className="flex h-9 w-9 items-center justify-center rounded-full transition-colors active:bg-white/[0.08]"
        >
          <Settings className="h-[20px] w-[20px] text-zinc-400" />
        </Link>
      </div>
    </div>
  );
});

const LandingLogo = memo(function LandingLogo() {
  return (
    <div className="mb-6 flex flex-col items-center desktop:mb-8">
      <Link to={Urls.demos()}>
        <img src={agenticaLogo} alt="Agentica" className="h-10 desktop:h-12 transition-opacity hover:opacity-80" />
      </Link>
    </div>
  );
});

export const ChatLandingView = (props: ChatLandingViewProps) => (
  <>
    {props.mobileIsland && props.mobileIsland.isAuthenticated && (
      <MobileLandingIsland mobileIsland={props.mobileIsland} />
    )}

    <div
      className="flex flex-col items-center"
      style={{ paddingTop: props.compact ? '2rem' : 'calc(50vh - 8rem)' }}
    >
      <LandingLogo />
      {props.statusBanners}
      {props.chatInput}
      <LandingNotices disconnected={props.disconnected} />

      <div className="mt-6 flex justify-center">
        <Link
          to={Urls.demos()}
          onClick={trackDemosClicked}
          className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.04] px-5 py-2.5 text-[13px] font-medium text-zinc-400 transition-colors hover:bg-white/[0.08] hover:text-zinc-200"
        >
          See demos
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  </>
);
